import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDocsetManifest } from "../../context/DocsetContext";
import { resolveResource } from "../../utils/resources";
import { APP_BASE_PATH, docsetPath } from "../../utils/basePath";
import type { DocNode } from "../../types/docset";
import type { ContentState } from "../../types/content";
import { Breadcrumbs, type Crumb } from "../navigation/Breadcrumbs";
import { ContentToc } from "./ContentToc";

type ContentViewportProps = {
  state: ContentState;
  activeNode?: DocNode;
};

export function ContentViewport({ state, activeNode }: ContentViewportProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const manifest = useDocsetManifest();
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const location = useLocation();
  const params = useParams();
  const docsetId = params.docsetId ?? manifest.id;

  // Always declare hooks first, guard inside effects
  useEffect(() => {
    if (state.status === "ready" && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [state.status]);

  useEffect(() => {
    if (state.status !== "ready" || !contentRef.current) return;
    const root = contentRef.current;
    const resolveAssetLike = (raw: string): string => {
      if (/^(?:https?:)?\/\//.test(raw)) return raw; // external
      // Absolute root path like "/assets/..." => map to docset base
      if (raw.startsWith("/")) {
        const trimmed = raw.replace(/^\/+/, "");
        return resolveResource(manifest, trimmed);
      }
      // Relative path
      const clean = raw.replace(/^\.\/?/, "");
      if (clean.startsWith("assets/")) {
        // Resolve to docset-level assets directory (assets/ at the root)
        return resolveResource(manifest, clean);
      }
      // Fallback: resolve relative to docset base
      return resolveResource(manifest, clean);
    };

    const fixSrc = (
      el: HTMLImageElement | HTMLSourceElement | HTMLMediaElement,
      attr: "src" | "data-src"
    ) => {
      const raw = el.getAttribute(attr);
      if (!raw) return;
      const resolved = resolveAssetLike(raw);
      el.setAttribute(attr, resolved);
    };

    const fixSrcSet = (el: HTMLImageElement | HTMLSourceElement) => {
      const raw = el.getAttribute("srcset");
      if (!raw) return;
      const parts = raw.split(",").map((p) => p.trim());
      const fixed = parts
        .map((part) => {
          const [url, descriptor] = part.split(/\s+/, 2);
          if (!url) return null;
          const resolved = resolveAssetLike(url);
          return descriptor ? `${resolved} ${descriptor}` : resolved;
        })
        .filter(Boolean)
        .join(", ");
      if (fixed) {
        el.setAttribute("srcset", fixed);
      }
    };

    root.querySelectorAll("img").forEach((img) => {
      fixSrc(img as HTMLImageElement, "src");
      fixSrcSet(img as HTMLImageElement);
    });
    root.querySelectorAll("source").forEach((source) => {
      fixSrc(source as HTMLSourceElement, "src");
      fixSrcSet(source as HTMLSourceElement);
    });
    root.querySelectorAll("a[href]").forEach((a) => {
      const el = a as HTMLAnchorElement;
      const href = el.getAttribute("href");
      if (!href || href.startsWith("#")) return;

      const raw = href.trim();

      // Heuristic: treat slug-like paths (no extension, not assets/) as internal
      // document links, including legacy values that were incorrectly prefixed
      // with "https://" by the editor.
      const resolveDocLinkPath = (): string | null => {
        if (!raw) return null;

        // Legacy bug: internal links stored as "https://<doc-path>"
        const httpsMatch = raw.match(/^https:\/\/(.+)$/i);
        let candidate = raw;
        if (httpsMatch) {
          candidate = httpsMatch[1];
        } else if (/^[a-z]+:/i.test(raw)) {
          // Other schemes (http:, mailto:, etc.) are treated as external.
          return null;
        }

        if (candidate.startsWith("/")) return null; // absolute path -> treat as asset
        const cleaned = candidate.replace(/^\.\/+/, "");
        if (cleaned.toLowerCase().startsWith("assets/")) return null;
        if (cleaned.includes(".")) return null; // has an extension -> likely asset/file
        return cleaned.replace(/^\/+/, "");
      };

      const docLinkPath = resolveDocLinkPath();

      if (docLinkPath) {
        if (docsetId) {
          el.setAttribute("href", docsetPath(encodeURIComponent(docsetId), docLinkPath));
        } else {
          el.setAttribute("href", `${APP_BASE_PATH}/${docLinkPath}`);
        }
        return;
      }

      const resolved = resolveAssetLike(raw);
      el.setAttribute("href", resolved);
    });
  }, [manifest, state.status, state, activeNode, docsetId]);

  // Scroll spy for headings
  useEffect(() => {
    if (state.status !== "ready" || !contentRef.current || !scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const headings = Array.from(
      contentRef.current.querySelectorAll<HTMLElement>(
        "h1[id], h2[id], h3[id], h4[id], h5[id], h6[id]"
      )
    );
    const computeActive = () => {
      const offset = 80; // header offset
      const scrollTop = container.scrollTop + offset;
      let current: string | null = null;
      for (const el of headings) {
        if (el.offsetTop <= scrollTop) {
          current = el.id;
        } else {
          break;
        }
      }
      setActiveSlug(current);
    };
    computeActive();
    const onScroll = () => computeActive();
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
    };
  }, [state.status]);

  // Scroll to hash (e.g. when clicking headings in left nav)
  useEffect(() => {
    if (state.status !== "ready" || !contentRef.current || !scrollContainerRef.current) return;
    const hash = location.hash?.replace(/^#/, "");
    if (!hash) return;
    const target = contentRef.current.querySelector<HTMLElement>(`#${CSS.escape(hash)}`);
    if (!target) return;
    const offset = 80;
    const top = target.offsetTop - offset;
    // Use setTimeout to ensure layout settled
    setTimeout(() => scrollContainerRef.current?.scrollTo({ top, behavior: "smooth" }), 0);
  }, [state.status, location.hash, activeNode]);

  const onSelectHeading = (slug: string) => {
    if (!contentRef.current || !scrollContainerRef.current) return;
    const target = contentRef.current.querySelector<HTMLElement>(`#${CSS.escape(slug)}`);
    if (!target) return;
    const offset = 80;
    const top = target.offsetTop - offset;
    scrollContainerRef.current.scrollTo({ top, behavior: "smooth" });
  };

  const trail: Crumb[] = useMemo(() => {
    if (!activeNode) return [];
    // Build trail from manifest root to active node
    const result: Crumb[] = [];
    const segments = activeNode.pathSegments;
    const walk = (nodes: DocNode[], prefix: string[] = []) => {
      for (const n of nodes) {
        const path = [...prefix, ...n.pathSegments.slice(prefix.length)];
        const isPrefix = segments.slice(0, path.length).join("/") === path.join("/");
        if (!isPrefix) continue;
        result.push({ title: n.title, pathSegments: n.pathSegments });
        if (n.children.length > 0) {
          walk(n.children, n.pathSegments);
        }
        if (n.pathSegments.join("/") === segments.join("/")) return;
      }
    };
    walk(manifest.nav);
    return result;
  }, [manifest.nav, activeNode]);

  if (state.status === "error") {
    return (
      <main className="flex flex-1 overflow-hidden">
        <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto px-6 text-center">
          <h2 className="text-xl font-semibold text-red-300">Failed to load document</h2>
          <p className="mt-2 max-w-lg text-sm text-slate-400">{state.error.message}</p>
        </div>
      </main>
    );
  }

  if (state.status === "loading" || state.status === "idle") {
    return (
      <main className="flex flex-1 overflow-hidden">
        <div className="flex flex-1 items-center justify-center overflow-y-auto">
          <div className="flex flex-col items-center gap-3">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-800 border-t-primary" />
            <p className="text-sm text-slate-400">Loading document…</p>
          </div>
        </div>
      </main>
    );
  }

  const { payload } = state;

  // (moved above)

  return (
    <main className="flex flex-1 overflow-hidden">
      <div className="flex-1 overflow-hidden">
        <div className="flex h-full gap-6 px-4 sm:px-6 lg:px-10 xl:px-12">
          <div
            ref={scrollContainerRef}
            className="doc-article-scroll min-w-0 flex-1 overflow-y-auto h-full"
          >
            <article className="doc-content pb-16 pt-0 bg-white text-slate-800">
              <Breadcrumbs trail={trail} />
              <h1 className="doc-page-title mt-3 mb-3 text-3xl font-semibold tracking-tight text-slate-900">
                {payload.title}
              </h1>
              <div ref={contentRef} dangerouslySetInnerHTML={{ __html: payload.html }} />
            </article>
          </div>
          <ContentToc
            headings={payload.headings}
            activeSlug={activeSlug ?? undefined}
            onSelect={onSelectHeading}
          />
        </div>
      </div>
    </main>
  );
}
